<template lang="pug">
//- Its own tab bar, like the ticket pages: a page with a form needs somewhere
//- to put a Save, and the shell's router-view cannot reach the bar's slot.
system-tabs(section="/system/settings")
    //- Circle buttons top-right, the way every other form in the admin does it.
    template(slot="tabbar-buttons")
        el-button(
            circle
            type="success"
            icon="el-icon-fa-save fas"
            :title="$t('Save')"
            :loading="savingSettings"
            @click="saveSettings"
        )
    el-card.contains-table(shadow="always")
        .platform-settings(v-loading="loading")
            //- Same intro block the communication connections page uses — h4 + .help,
            //- not a hand-rolled heading.
            .section-intro
                h4 {{ $t('Platform mailbox') }}
                p.help {{ $t('The mailbox CityOffer works from. Every system email leaves through this connection instead of the server configuration: password resets, invoices, notifications. Mail arriving here opens tickets on our support desk, and answers to our replies land back in their ticket.') }}

            //- One connection, ours. Tenants' mailboxes are managed in their own
            //- settings and are none of this screen's business.
            .connection(v-if="mailbox" @click="openMailbox")
                .connection__identity
                    i.connection__icon.el-icon-fa-headset.fas
                    .connection__text
                        span.connection__name {{ mailbox.name || $t('Platform mailbox') }}
                        span.connection__address {{ mailbox.address || $t('No sender address set') }}
                .connection__state
                    el-tag(v-if="mailbox.imap_enabled" size="mini" type="success" effect="dark") {{ $t('Sends and receives') }}
                    el-tag(v-else size="mini" type="warning" effect="dark") {{ $t('Sends only') }}
                    el-tag(v-if="mailbox.is_validated" size="mini" type="success" effect="plain") {{ $t('Validated') }}
                    el-tag(v-else-if="mailbox.is_faulty" size="mini" type="danger" effect="plain") {{ $t('Faulty') }}
                    el-tag(v-else size="mini" type="info" effect="plain") {{ $t('Not validated') }}
                .connection__actions
                    el-tooltip(:content="$t('Edit')" placement="top")
                        el-button(plain circle size="mini" type="primary" icon="el-icon-fa-pen fas" @click.stop="openMailbox")
                    el-tooltip(:content="$t('Remove')" placement="top")
                        el-button.delete-button(plain circle size="mini" type="danger" icon="el-icon-fa-trash-alt far" @click.stop="$refs.deleteDialog.open(mailbox)")

            .empty-state(v-else-if="!loading")
                i.empty-state__icon.el-icon-fa-envelope-open-text.fas
                p.empty-state__text {{ $t('No platform mailbox yet. Without one, system email keeps using the server configuration and customers cannot reach us by email.') }}
                el-button(type="primary" size="mini" icon="el-icon-fa-plus fas" @click="openMailbox") {{ $t('Set up platform mailbox') }}

            //- A validation badge describes the last connection TEST, which reads
            //- the form. This reads the stored row, so it is the one that says
            //- whether anything can actually log in.
            el-alert.receiving-hint(
                v-if="mailbox && mailbox.has_password === false"
                type="error"
                :closable="false"
                show-icon
                :title="$t('No password is stored for this mailbox. Sending and ticket intake both fail until you open the connection, enter the password and save this page.')"
            )

            el-alert.receiving-hint(
                v-if="mailbox && !mailbox.imap_enabled"
                type="warning"
                :closable="false"
                show-icon
                :title="$t('Receiving is off. Turn IMAP on in the connection, otherwise no email reaches the ticket system.')"
            )

            //- The SAME mailbox form every connection uses (SMTP, IMAP, OAuth,
            //- validation, password handling) — inline, so it binds to our draft.
            full-height-drawer(
                :visible.sync="drawerVisible"
                :title="$t('Platform mailbox')"
                size="620px"
                destroy-on-close
                @open="onDrawerOpen"
            )
                el-form.mailbox-form(label-position="top" :model="draft" :rules="mailboxRules" ref="mailboxForm")
                    smtp-credentials(
                        :cms="syntheticCms"
                        scope="mailbox"
                        inline
                        :show-signature="false"
                    )
                template(#footer-left)
                    el-button(round size="mini" icon="el-icon-fa-times fas" @click="cancel") {{ $t('Cancel') }}
                template(#footer)
                    //- Ok, not Save: the page's Save is what writes it.
                    el-button(round size="mini" type="success" icon="el-icon-fa-check fas" @click="save") {{ $t('Ok') }}

            //- Our own Textvorlagen. No cms-id prop: on this screen the owner IS us,
            //- and the manager reads a null owner as exactly that.
            message-preset-manager(
                ref="messagePresets"
                :mode="suggestionMode"
                :presets="messagePresets"
                @update:mode="value => suggestionMode = value"
                @update:presets="value => messagePresets = value"
            )

            confirm-delete-dialog(
                ref="deleteDialog"
                :title="$t('Remove platform mailbox')"
                :message="$t('System email falls back to the server configuration and ticket intake stops. Existing tickets are kept.')"
                @confirm="remove"
            )

    //- And again at the bottom right, the way the ticket form does — a page is
    //- read to the end, and that is where the Save has to be waiting. Plain
    //- `.form-buttons` (already flex-end) rather than `.buttons-flex`, which is
    //- space-between and would park a single group on the LEFT.
    .form-buttons
        el-button(
            round
            type="success"
            icon="el-icon-fa-save fas"
            :loading="savingSettings"
            @click="saveSettings"
        ) {{ $t('Save') }}
</template>
<i18n>
{
    "de": {
        "Platform mailbox": "Plattform-Postfach",
        "The mailbox CityOffer works from. Every system email leaves through this connection instead of the server configuration: password resets, invoices, notifications. Mail arriving here opens tickets on our support desk, and answers to our replies land back in their ticket.": "Das Postfach, mit dem CityOffer arbeitet. Alle Systemmails werden über diese Verbindung statt über die Serverkonfiguration versendet: Passwort-Zurücksetzungen, Rechnungen, Benachrichtigungen. Eingehende E-Mails eröffnen Tickets auf unserem Support-Desk, und Antworten auf unsere Rückmeldungen landen wieder im jeweiligen Ticket.",
        "No sender address set": "Keine Absenderadresse hinterlegt",
        "Sends and receives": "Versand und Empfang",
        "Sends only": "Nur Versand",
        "Validated": "Verifiziert",
        "Faulty": "Fehlerhaft",
        "Not validated": "Nicht verifiziert",
        "Edit": "Bearbeiten",
        "Remove": "Entfernen",
        "No platform mailbox yet. Without one, system email keeps using the server configuration and customers cannot reach us by email.": "Noch kein Plattform-Postfach eingerichtet. Ohne Postfach laufen Systemmails weiter über die Serverkonfiguration und es erreichen uns keine Kunden-E-Mails.",
        "Set up platform mailbox": "Plattform-Postfach einrichten",
        "Receiving is off. Turn IMAP on in the connection, otherwise no email reaches the ticket system.": "Der Empfang ist deaktiviert. Aktiviere IMAP in der Verbindung, sonst erreichen keine E-Mails das Ticketsystem.",
        "No password is stored for this mailbox. Sending and ticket intake both fail until you open the connection, enter the password and save this page.": "Für dieses Postfach ist kein Passwort gespeichert. Versand und Ticket-Eingang schlagen fehl, bis du die Verbindung öffnest, das Passwort einträgst und diese Seite speicherst.",
        "Cancel": "Abbrechen",
        "Save": "Speichern",
        "Ok": "Ok",
        "Settings saved": "Einstellungen gespeichert",
        "Remove platform mailbox": "Plattform-Postfach entfernen",
        "System email falls back to the server configuration and ticket intake stops. Existing tickets are kept.": "Systemmails laufen wieder über die Serverkonfiguration und der Ticket-Eingang wird gestoppt. Bestehende Tickets bleiben erhalten.",
        "Platform mailbox saved": "Plattform-Postfach gespeichert",
        "System email and ticket intake now use this connection.": "Systemmails und Ticket-Eingang laufen ab sofort über diese Verbindung.",
        "Platform mailbox removed": "Plattform-Postfach entfernt",
        "System email falls back to the server configuration.": "Systemmails laufen wieder über die Serverkonfiguration.",
        "Error": "Fehler",
        "The platform mailbox could not be saved.": "Das Plattform-Postfach konnte nicht gespeichert werden."
    }
}
</i18n>

<script>
import _ from 'lodash';

import FullHeightDrawer from '@/components/FullHeightDrawer';
import ConfirmDeleteDialog from '@/components/ConfirmDeleteDialog';
import SmtpCredentials from '@/views/settings/Account/Form/Partials/SmtpCredentials';
import MessagePresetManager from '@/extensions/communication/views/MessagePreset/Manager';
import SystemTabs from '@/views/system/Partials/Tabs';
import emptyMailbox from '@/extensions/communication/utils/emptyMailbox';
import PlatformSettingsApi from '@/store/api/system/platformSettings';

export default {
    name: 'PlatformSettings',
    components: {
        ConfirmDeleteDialog,
        FullHeightDrawer,
        SmtpCredentials,
        'message-preset-manager': MessagePresetManager,
        SystemTabs,
    },
    data() {
        return {
            mailbox: null,
            // The object the shared form binds to. Fully keyed from the start,
            // or Vue 2 computeds never subscribe to the missing keys and the
            // inputs freeze (see emptyMailbox).
            draft: emptyMailbox(),
            drawerVisible: false,
            loading: false,
            savingSettings: false,
            suggestionMode: 'off',
            // Our own Textvorlagen. The manager edits this list; nothing here
            // reaches the server until Save.
            messagePresets: [],
            // The trash stages a removal; Save carries it out. Without this the
            // page could not tell "no mailbox yet" from "remove the one we had".
            mailboxRemoved: false,
            saving: false,
        };
    },
    computed: {
        // SmtpCredentials reads its model from cms[scope]; the platform mailbox
        // has no CMS, so it gets a stand-in carrying just the draft.
        syntheticCms() {
            return { id: null, mailbox: this.draft };
        },
        // vee-element renders a form-item's field only when its `prop` has a
        // rule, so the SMTP inputs stay INVISIBLE without this — the same
        // pipe-string set the communication connections drawer uses.
        mailboxRules() {
            if (String(this.draft.auth_method || 'password').startsWith('oauth')) {
                return {};
            }

            const rules = {
                host: 'required',
                port: 'required|numeric',
                username: 'required',
            };

            // An existing mailbox keeps its stored secret; a new one must type it.
            if (!this.draft.id) rules.password = 'required';

            return rules;
        },
    },
    created() {
        this.fetch();
    },
    methods: {
        fetch() {
            this.loading = true;

            return PlatformSettingsApi.fetchPlatformSettings()
                .then((settings) => {
                    this.mailbox = _.get(settings, 'mailbox') || null;
                    this.suggestionMode = _.get(settings, 'preset_suggestion_mode') || 'off';
                    this.messagePresets = _.get(settings, 'messagePresets') || [];
                    this.mailboxRemoved = false;
                })
                .catch((error) => this.reportError(error))
                .finally(() => {
                    this.loading = false;
                });
        },
        openMailbox() {
            this.drawerVisible = true;
        },
        onDrawerOpen() {
            const stored = this.mailbox ? _.omit(this.mailbox, ['__typename']) : {};
            this.draft = Object.assign(emptyMailbox(), stored);

            // Seeding the fields fires el-form-item's change-validation, so a
            // freshly opened drawer would show its required fields already red.
            this.$nextTick(() => {
                const form = this.$refs.mailboxForm;
                if (form && form.clearValidate) form.clearValidate();
            });
        },
        cancel() {
            this.drawerVisible = false;
        },
        /**
         * Ok, not Save: the drawer puts the mailbox into the page's state and
         * the page's Save writes it, with the rest of the platform settings.
         */
        save() {
            this.$refs.mailboxForm.validate((valid) => {
                if (!valid) return;

                this.mailbox = _.omit(_.cloneDeep(this.draft), ['__typename', 'is_platform_mailbox']);
                this.mailboxRemoved = false;
                this.drawerVisible = false;
            });
        },
        /** Also staged — the deletion happens on Save, like every other change. */
        remove() {
            this.mailbox = null;
            this.mailboxRemoved = true;
        },
        /**
         * The page's Save, in both places the admin puts one — and the ONLY
         * thing on this screen that writes. The mailbox drawer, the trash and
         * the preset drawer all stage into state; this persists the lot.
         */
        saveSettings() {
            this.savingSettings = true;

            const settings = {
                preset_suggestion_mode: this.suggestionMode,
                messagePresets: this.messagePresets,
            };

            if (this.mailboxRemoved) {
                settings.mailbox = { deleted: true };
            } else if (this.mailbox) {
                settings.mailbox = _.omit(_.cloneDeep(this.mailbox), ['__typename', 'is_platform_mailbox']);
            }

            PlatformSettingsApi.savePlatformSettings(settings)
                .then((saved) => {
                    // Replace the state with what was actually written, so the
                    // staged rows pick up their real ids.
                    this.mailbox = _.get(saved, 'mailbox') || null;
                    this.messagePresets = _.get(saved, 'messagePresets') || [];
                    this.mailboxRemoved = false;

                    this.notify(this.$t('Settings saved'), null, 'el-icon-fa-check fas');
                })
                .catch((error) => this.reportError(error))
                .finally(() => {
                    this.savingSettings = false;
                });
        },
        notify(title, message, iconClass) {
            this.$notify({
                title,
                message,
                type: 'success',
                position: 'bottom-left',
                customClass: 'dark',
                iconClass,
                duration: 8000,
            });
        },
        reportError(error) {
            this.$notify({
                title: this.$t('Error'),
                message: _.get(error, 'message') || this.$t('The platform mailbox could not be saved.'),
                type: 'error',
                position: 'bottom-left',
                customClass: 'dark',
                iconClass: 'el-icon-fa-xmark fas',
                duration: 8000,
            });
        },
    },
};
</script>

<style lang="sass" scoped>
.platform-settings
    // The system tab panel IS the card and zeroes its body padding, so the
    // page carries its own 15px — the same as ProviderCosts/List.vue.
    padding: 15px

    .section-intro
        margin-bottom: 1rem

        h4
            margin: 0 0 4px
            color: $color-grey-text

        // Styling comes from the global `.help` rule — no sizes invented here.
        .help
            margin: 0

    .connection
        display: flex
        align-items: center
        gap: 20px
        padding: 14px 16px
        border: 1px solid #ebeef5
        border-radius: 4px
        cursor: pointer
        transition: border-color .2s, box-shadow .2s

        &:hover
            border-color: #c6e2ff
            box-shadow: 0 2px 8px rgba(0, 0, 0, .06)

        &__identity
            display: flex
            align-items: center
            gap: 12px
            flex: 1
            min-width: 0

        &__icon
            font-size: 18px
            color: #409EFF

        &__text
            display: flex
            flex-direction: column
            min-width: 0

        &__name
            font-weight: 500
            color: #303133

        &__address
            font-size: 12px
            color: #909399
            overflow: hidden
            text-overflow: ellipsis
            white-space: nowrap

        &__state
            display: flex
            align-items: center
            gap: 6px

        &__actions
            display: flex
            gap: 6px

    .empty-state
        display: flex
        flex-direction: column
        align-items: center
        gap: 12px
        padding: 40px 20px
        border: 1px dashed #dcdfe6
        border-radius: 4px
        text-align: center

        &__icon
            font-size: 26px
            color: #c0c4cc

        &__text
            margin: 0
            max-width: 420px
            color: #909399
            line-height: 1.5

    .receiving-hint
        margin-top: 15px
</style>
